// src/pages/DocumentsChecklist.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  CheckCircle, XCircle, ArrowLeft, Save, RefreshCw,
  User, Mail, Phone, Calendar, MapPin, BookOpen, IdCard,
  FileText, Wallet, Upload, Download, Printer, Eye, X
} from 'lucide-react';
import SummaryApi from '../common/index';
import paymentscan from "../assets/paymentscan.jpg";
import ghrLogo from "../assets/ghru-logo2.png";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const DocumentsChecklist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedStudentId = queryParams.get('id');
  const studentId = encodedStudentId ? atob(encodedStudentId) : null;

  // Single unified loader for the whole page
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [checklistData, setChecklistData] = useState(null);
  const [checklistId, setChecklistId] = useState(null);
  const [checklistExists, setChecklistExists] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [onboardingFeeId, setOnboardingFeeId] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // ── Application Form modal state (same concept as Student Report) ──
  const [showAppFormModal, setShowAppFormModal] = useState(false);
  const [generatingAppFormPdf, setGeneratingAppFormPdf] = useState(false);
  const appFormRef = useRef(null);

  // Payment form state
  const [paymentData, setPaymentData] = useState({
    payment_mode: 'online',
    amount: '',
    utr_number: '',
    transaction_id: '',
    payment_receipt: null,
  });
  const [preview, setPreview] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const receiptRef = useRef(null);

  // Document checklist items - Mapping to database fields
  const documentItems = [
    { key: 'student_aadhar', label: 'Aadhaar Card – Student', dbField: 'is_aadhar' },
    { key: 'ssc_memo', label: 'SSC / 10th Marks Memo & Certificate', dbField: 'is_ssc_certificate' },
    { key: 'inter_memo', label: 'Intermediate (10+2) Marks Memo & Certificate', dbField: 'is_inter_certificate' },
    { key: 'passport_photo', label: '8 Recent Passport Size Colour Photographs', dbField: 'is_passport_photo' },
    { key: 'tc', label: 'Transfer Certificate (TC)', dbField: 'is_tc' },
    { key: 'study_certificate', label: 'Study / Bonafide Certificate (if applicable)', dbField: 'is_study_certificate' },
    { key: 'income_certificate', label: 'Income Certificate (Latest, if applicable)', dbField: 'is_income_certificate' },
    { key: 'residence_certificate', label: 'Residence / Domicile Certificate (if applicable)', dbField: 'is_residence_certificate' },
    { key: 'jee_certificate', label: 'JEE / EMCET RANK CARD', dbField: 'is_jee_certificate' },
    { key: 'migration_certificate', label: 'MIGRATION CERTIFICATE', dbField: 'is_migration_certificate' },
  ];

  // ===== SEQUENTIAL PAGE INITIALIZATION =====
  useEffect(() => {
    const initPage = async () => {
      if (!studentId) {
        toast.error('Student ID is missing');
        navigate('/student-report');
        return;
      }

      setPageLoading(true);
      try {
        // 1. Fetch student data first
        await fetchStudentData();

        // 2. Fetch payment details
        await fetchPaymentDetails();

        // 3. Fetch checklist data
        await fetchChecklistData();
      } catch (err) {
        console.error('Page init error:', err);
      } finally {
        setPageLoading(false);
      }
    };

    initPage();
  }, [studentId]);

  // ===== FETCH STUDENT DATA =====
  const fetchStudentData = async () => {
    try {
      const res = await fetch(SummaryApi.getstudentByid.url, {
        method: SummaryApi.getstudentByid.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const result = await res.json();
      if (result.status === true && result.data) {
        const student = result.data;
        if (student.date_of_birth) {
          student.date_of_birth = new Date(student.date_of_birth)
            .toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
            .replace(/ /g, '-');
        }
        setStudentData(student);
      } else {
        toast.error(result.message || 'Failed to load student data');
      }
    } catch (err) {
      console.error('Fetch student error:', err);
      toast.error('Error loading student details');
    }
  };

  // ===== FETCH CHECKLIST DATA =====
  const fetchChecklistData = async () => {
    try {
      const res = await fetch(SummaryApi.getchecklist.url, {
        method: SummaryApi.getchecklist.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
      });

      const result = await res.json();
      console.log('Checklist response:', result);
      
      if (result.status === true && result.data) {
        const data = result.data;
        
        // Check if data has check_list_id
        if (data && data.check_list_id) {
          setChecklistExists(true);
          setChecklistId(data.check_list_id);
          // Map database fields to frontend keys
          setChecklistData({
            student_aadhar: parseInt(data.is_aadhar) || 0,
            ssc_memo: parseInt(data.is_ssc_certificate) || 0,
            inter_memo: parseInt(data.is_inter_certificate) || 0,
            passport_photo: parseInt(data.is_passport_photo) || 0,
            tc: parseInt(data.is_tc) || 0,
            study_certificate: parseInt(data.is_study_certificate) || 0,
            income_certificate: parseInt(data.is_income_certificate) || 0,
            residence_certificate: parseInt(data.is_residence_certificate) || 0,
            jee_certificate: parseInt(data.is_jee_certificate) || 0,
            migration_certificate: parseInt(data.is_migration_certificate) || 0,
          });
        } else {
          setChecklistExists(false);
          setChecklistData({
            student_aadhar: 0,
            ssc_memo: 0,
            inter_memo: 0,
            passport_photo: 0,
            tc: 0,
            study_certificate: 0,
            income_certificate: 0,
            residence_certificate: 0,
            jee_certificate: 0,
            migration_certificate: 0,
          });
        }
      } else {
        setChecklistExists(false);
        setChecklistData({
          student_aadhar: 0,
          ssc_memo: 0,
          inter_memo: 0,
          passport_photo: 0,
          tc: 0,
          study_certificate: 0,
          income_certificate: 0,
          residence_certificate: 0,
          jee_certificate: 0,
          migration_certificate: 0,
        });
      }
    } catch (err) {
      console.error('Fetch checklist error:', err);
      setChecklistExists(false);
      setChecklistData({
        student_aadhar: 0,
        ssc_memo: 0,
        inter_memo: 0,
        passport_photo: 0,
        tc: 0,
        study_certificate: 0,
        income_certificate: 0,
        residence_certificate: 0,
        jee_certificate: 0,
        migration_certificate: 0,
      });
    }
  };

  // ===== SAVE OR UPDATE CHECKLIST =====
  const handleSaveChecklist = async () => {
    // 🚫 Prevent saving if payment is not completed
    if (!paymentSuccess) {
      toast.error("⚠️ Please complete the onboarding fee payment first before saving the checklist.");
      return;
    }

    if (!checklistData) return;

    setSaving(true);
    try {
      // Map to database field names
      const payload = {
        student_id: studentId,
        is_aadhar: checklistData.student_aadhar || 0,
        is_ssc_certificate: checklistData.ssc_memo || 0,
        is_inter_certificate: checklistData.inter_memo || 0,
        is_passport_photo: checklistData.passport_photo || 0,
        is_tc: checklistData.tc || 0,
        is_study_certificate: checklistData.study_certificate || 0,
        is_income_certificate: checklistData.income_certificate || 0,
        is_residence_certificate: checklistData.residence_certificate || 0,
        is_jee_certificate: checklistData.jee_certificate || 0,
        is_migration_certificate: checklistData.migration_certificate || 0,
      };

      let url = SummaryApi.addchecklist.url;
      let method = SummaryApi.addchecklist.method;

      // If checklist exists, use update endpoint
      if (checklistExists && checklistId) {
        url = SummaryApi.updatechecklist.url;
        method = SummaryApi.updatechecklist.method;
        payload.check_list_id = checklistId;
      }

      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      console.log('Save/Update response:', result);
      
      if (result.status === true) {
        toast.success(result.message || (checklistExists ? 'Checklist updated successfully!' : 'Checklist saved successfully!'));
        if (result.check_list_id) {
          setChecklistId(result.check_list_id);
          setChecklistExists(true);
        }
        await fetchChecklistData();
      } else {
        toast.error(result.message || 'Failed to save checklist');
      }
    } catch (err) {
      console.error('Save checklist error:', err);
      toast.error('Error saving checklist');
    } finally {
      setSaving(false);
    }
  };

  // ===== FETCH PAYMENT DETAILS =====
  const fetchPaymentDetails = async () => {
    try {
      const res = await fetch(SummaryApi.getonboardpaymentdetails.url, {
        method: SummaryApi.getonboardpaymentdetails.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
      });
      
      const result = await res.json();
      console.log('Payment details response:', result);
      
      if (result.status === true && result.data && result.data.length > 0) {
        const data = result.data[0];
        if (data.is_paid === '1' || data.is_paid === 1) {
          setPaymentSuccess(true);
          setPaymentDetails(data);
          setOnboardingFeeId(data.onboarding_fee_id);
        } else {
          setPaymentSuccess(false);
        }
      } else {
        setPaymentSuccess(false);
      }
    } catch (err) {
      console.error('Error fetching payment details:', err);
      setPaymentSuccess(false);
    }
  };

  // ===== TOGGLE CHECKLIST ITEM =====
  const toggleChecklistItem = (key) => {
    setChecklistData(prev => ({
      ...prev,
      [key]: prev[key] === 1 ? 0 : 1
    }));
  };

  // ===== CHECK IF AT LEAST ONE ITEM IS SELECTED =====
  const isAtLeastOneItemSelected = () => {
    if (!checklistData) return false;
    return documentItems.some(item => checklistData[item.key] === 1);
  };

  // ===== PAYMENT HANDLERS =====
  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentModeChange = (mode) => {
    setPaymentData((prev) => ({ 
      ...prev, 
      payment_mode: mode,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPaymentData((prev) => ({ ...prev, payment_receipt: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validatePaymentForm = () => {
    if (!paymentData.payment_mode) {
      toast.error("Please select payment mode");
      return false;
    }
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return false;
    }
    
    // Online mode validations
    if (paymentData.payment_mode === 'online') {
      if (!paymentData.utr_number.trim()) {
        toast.error("UTR Number is required for online payment");
        return false;
      }
      if (!paymentData.transaction_id.trim()) {
        toast.error("Transaction ID is required for online payment");
        return false;
      }
      if (!paymentData.payment_receipt) {
        toast.error("Payment receipt image is required for online payment");
        return false;
      }
    }
    
    // Offline mode - only amount is required (UTR, Transaction ID, receipt are optional)
    if (paymentData.payment_mode === 'offline') {
      // No additional validations needed
    }
    
    if (!studentId) {
      toast.error("Student ID is missing");
      return false;
    }
    return true;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!validatePaymentForm()) return;
    
    setPaymentLoading(true);
    try {
      const formData = new FormData();
      formData.append('student_id', studentId);
      formData.append('amount', paymentData.amount);
      formData.append('payment_mode', paymentData.payment_mode);
      
      // For online mode, send all fields
      if (paymentData.payment_mode === 'online') {
        formData.append('transaction_id', paymentData.transaction_id);
        formData.append('utr_number', paymentData.utr_number);
        formData.append('payment_receipt', paymentData.payment_receipt);
      } else {
        // For offline mode, send optional fields only if they have values
        if (paymentData.transaction_id) {
          formData.append('transaction_id', paymentData.transaction_id);
        }
        if (paymentData.utr_number) {
          formData.append('utr_number', paymentData.utr_number);
        }
        if (paymentData.payment_receipt) {
          formData.append('payment_receipt', paymentData.payment_receipt);
        }
      }

      const res = await fetch(SummaryApi.createonboardingpayment.url, {
        method: SummaryApi.createonboardingpayment.method,
        body: formData,
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const result = await res.json();
      const isSuccess = result.success === true || result.status === true;

      if (isSuccess) {
        toast.success(result.message || "Onboarding fee payment completed successfully!");
        
        await fetchPaymentDetails();
        
        setPaymentData({
          payment_mode: 'online',
          amount: '',
          utr_number: '',
          transaction_id: '',
          payment_receipt: null,
        });
        setPreview(null);
        if (receiptRef.current) {
          receiptRef.current.value = '';
        }
      } else {
        toast.error(result.message || "Payment creation failed");
      }
    } catch (err) {
      console.error("Payment submission error:", err);
      toast.error(err.message || "Something went wrong");
    } finally {
      setPaymentLoading(false);
    }
  };

  // ===== RECEIPT PDF GENERATION =====
// ===== RECEIPT PDF GENERATION - OPTIMIZED VERSION =====
const generateReceiptPDF = () => {
  if (!paymentDetails && !studentData) return null;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const cardX = margin;
  const cardY = 28;
  const cardW = pageWidth - margin * 2;

  // Background
  doc.setFillColor(242, 242, 242);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // White card
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(cardX, cardY, cardW, 580, 8, 8, 'F');
  doc.setFillColor(245, 245, 245);
  doc.rect(cardX, cardY, cardW, 18, 'F');

  let y = cardY + 35;

  // Logo (unchanged)
  try {
    const img = new Image();
    img.src = ghrLogo;
    const logoCanvas = document.createElement('canvas');
    logoCanvas.width = img.width;
    logoCanvas.height = img.height;
    const ctx = logoCanvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const logoDataUrl = logoCanvas.toDataURL('image/png');
    const logoH = 45;
    const logoW = (img.width / img.height) * logoH;
    doc.addImage(logoDataUrl, 'PNG', margin + 10, y - 8, logoW, logoH);
  } catch (_) {}

  // --- AMOUNT (TOP RIGHT) ---
  const paymentAmount = paymentDetails?.amount || '0';
  const paymentMode = paymentDetails?.payment_mode || 'Online';

  // Show as "RS 500.00" – no ₹ symbol
  const amountText = `RS ${paymentAmount}`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(17, 17, 17);
  doc.text(amountText, pageWidth - margin - 10, y + 10, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(119, 119, 119);
  doc.text(`via ${paymentMode.toUpperCase()}`, pageWidth - margin - 10, y + 28, { align: 'right' });

  y += 80;

  // University name (unchanged)
  doc.setFont('times', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(229, 57, 53);
  doc.text('raisoni', margin + 12, y);
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setCharSpace(3);
  doc.setTextColor(229, 57, 53);
  doc.text('EDUCATION', margin + 12, y + 8);
  doc.setCharSpace(0);

  y += 25;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(16);
  doc.setTextColor(46, 125, 50);
  doc.text('Your payment successfully completed.', margin + 12, y);

  y += 22;

  // Main content box (unchanged)
  const boxX = margin + 10;
  const boxW = cardW - 20;
  const studentName = paymentDetails?.student_name || studentData?.student_name || 'Student';
  const studentEmail = paymentDetails?.email || studentData?.email || 'N/A';

  doc.setDrawColor(221, 221, 221);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(boxX, y, boxW, 85, 5, 5, 'FD');

  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(51, 51, 51);
  const hiW = doc.getTextWidth('Hi ');
  doc.text('Hi ', boxX + 14, y);
  doc.setFont('helvetica', 'bold');
  doc.text(`${studentName},`, boxX + 14 + hiW, y);

  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(51, 51, 51);
  doc.text('Thanks for using RAISONI!', boxX + 14, y);

  y += 16;
  doc.setFontSize(11);
  doc.text('For any further queries you can get in touch with the merchant on', boxX + 14, y);

  y += 14;
  doc.setTextColor(26, 115, 232);
  doc.text(studentEmail, boxX + 14, y);

  y += 32;

  // --- DETAILS TABLE ---
  const col1 = margin + 12;
  const col2 = margin + 12 + cardW / 2 - 20;
  const labelClr = [119, 119, 119];
  const valClr = [34, 34, 34];

  // Row 1: Transaction ID & Email
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...labelClr);
  doc.text('Transaction ID:', col1, y);
  doc.text('Email:', col2, y);

  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...valClr);
  doc.text(paymentDetails?.transaction_id || 'N/A', col1, y);
  doc.setTextColor(26, 115, 232);
  doc.text(studentEmail, col2, y);

  y += 24;

  // Row 2: Contact Number & UTR Number
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...labelClr);
  doc.text('Contact Number:', col1, y);
  doc.text('UTR Number:', col2, y);

  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...valClr);
  doc.text(paymentDetails?.mobile_number || studentData?.mobile_number || 'N/A', col1, y);
  doc.text(paymentDetails?.utr_number || 'N/A', col2, y);

  y += 24;

  // Row 3: Paid Amount & Payment Date (NO extra "RS" after the amount)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...labelClr);
  doc.text('Paid Amount:', col1, y);
  doc.text('Payment Date:', col2, y);

  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...valClr);

  // Show as "RS 500.00" – no ₹, no separate "RS"
  const paidAmount = `RS ${paymentAmount}`;
  doc.text(paidAmount, col1, y);

  doc.text(
    paymentDetails?.payment_date
      ? new Date(paymentDetails.payment_date).toLocaleString('en-IN')
      : new Date().toLocaleString('en-IN'),
    col2,
    y
  );

  y += 24;

  // Row 4: Payment Mode
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...labelClr);
  doc.text('Payment Mode:', col1, y);

  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...valClr);
  doc.text(paymentMode.toUpperCase(), col1, y);

  y += 30;
  doc.setDrawColor(229, 229, 229);
  doc.setLineWidth(0.8);
  doc.line(margin + 12, y, pageWidth - margin - 12, y);

  return doc;
};

  const handleDownloadReceiptPDF = async () => {
    if (!paymentDetails && !studentData) {
      toast.error('No payment details available');
      return;
    }
    
    setGeneratingPdf(true);
    try {
      const doc = generateReceiptPDF();
      if (doc) {
        doc.save(`Onboarding_Receipt_${onboardingFeeId || 'unknown'}_${Date.now()}.pdf`);
        toast.success('Receipt PDF downloaded successfully!');
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF receipt. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handlePrintReceipt = () => {
    if (!paymentDetails && !studentData) {
      toast.error('No payment details available');
      return;
    }

    const doc = generateReceiptPDF();
    if (doc) {
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = function() {
          printWindow.print();
        };
      }
    }
  };

  const handleViewReceipt = async () => {
    if (!paymentDetails && !studentData) {
      toast.error('No payment details available');
      return;
    }
    
    const doc = generateReceiptPDF();
    if (doc) {
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      setReceiptImageUrl(url);
      setShowReceiptModal(true);
    } else {
      toast.error('Failed to generate receipt');
    }
  };

  const handleBack = () => {
    navigate('/student-report');
  };

  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    if (receiptImageUrl) {
      URL.revokeObjectURL(receiptImageUrl);
      setReceiptImageUrl(null);
    }
  };

  // ── Application Form modal handlers (same concept as Student Report) ──
  const handleViewApplicationForm = () => {
    if (!studentData) {
      toast.error('Student details are still loading. Please wait.');
      return;
    }
    setShowAppFormModal(true);
  };

  const closeAppFormModal = () => {
    setShowAppFormModal(false);
  };

  // Print only the application-form content
  const handlePrintApplicationForm = () => {
    window.print();
  };

  // Capture the exact on-screen application form and export as PDF - SINGLE PAGE
  const handleDownloadApplicationFormPDF = async () => {
    if (!appFormRef.current || !studentData) return;
    setGeneratingAppFormPdf(true);
    try {
      const canvas = await html2canvas(appFormRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: appFormRef.current.scrollWidth,
        height: appFormRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        unit: 'px',
        format: [canvas.width, canvas.height],
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(
        `Application_Form_${studentData.student_name || 'student'}_${studentId || 'unknown'}.pdf`,
      );
      toast.success('Application form PDF downloaded successfully!');
    } catch (err) {
      console.error('Application form PDF generation error:', err);
      toast.error('Failed to generate application form PDF. Please try again.');
    } finally {
      setGeneratingAppFormPdf(false);
    }
  };

  // Calculate progress
  const totalItems = documentItems.length;
  const checkedItems = checklistData ? Object.values(checklistData).filter(v => v === 1).length : 0;
  const progress = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;

  // Format display value
  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '-' || value === 'N/A')
      return '—';
    return value;
  };

  const inputClass = 'block w-full border border-gray-300 rounded px-3 py-1.5 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none';
  const labelClass = 'block text-xs font-medium text-gray-700 mb-0.5';
  
  // For offline mode, fields are optional - style them differently
  const isOnlineMode = paymentData.payment_mode === 'online';
  const optionalFieldClass = isOnlineMode ? '' : 'opacity-60';

  // Reset All handler
  const handleResetAll = () => {
    const resetData = {};
    documentItems.forEach(item => {
      resetData[item.key] = 0;
    });
    setChecklistData(resetData);
  };

  // Check All handler
  const handleCheckAll = () => {
    const checkAll = {};
    documentItems.forEach(item => {
      checkAll[item.key] = 1;
    });
    setChecklistData(checkAll);
  };

  // 🔥 UPDATED: Check if save button should be disabled 
  // Disabled if: saving, OR payment not done, OR no item selected
  const isSaveDisabled = saving || !paymentSuccess || !isAtLeastOneItemSelected();

  return (
    <div className="max-w-7xl mx-auto px-4 py-3">
      {/* Print styles - same concept as Student Report's Application Form print block */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #application-form-print, #application-form-print * { visibility: visible; }
          #application-form-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            padding: 15px !important;
          }
          .no-print { display: none !important; }
          .app-form-header {
            background: #3b1a6b !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .app-form-subheader {
            background: #4e2280 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .app-form-gold-text {
            color: #f5d87a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .app-form-success {
            background: #f0fdf4 !important;
            border-color: #bbf7d0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="bg-white shadow-lg rounded-xl p-4 md:p-5 border border-gray-200">
        {/* ===== HEADER WITH LOGO CENTERED ===== */}
        <div className="flex items-center justify-between mb-3 border-b pb-2">
          <div className="flex-1"></div>
          <div className="flex items-center gap-3">
            <img
              src={ghrLogo}
              alt="GH Raisoni University"
              className="h-10 w-auto"
            />
            <h1 className="text-lg font-bold text-gray-800">
        Student    Onboarding  Report
            </h1>
          </div>
          <div className="flex-1 flex justify-end">

          </div>
        </div>

        {/* ===== SINGLE LOADER GATES THE ENTIRE PAGE ===== */}
        {pageLoading ? (
          <div className="text-center py-6">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="mt-1 text-xs text-gray-600">Loading student details...</p>
          </div>
        ) : (
          <>
            {/* ===== SECTION 1: COURSE APPLYING FOR ===== */}
            <div className="mb-3">
              <div className="bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-t-md">
                COURSE APPLYING FOR
              </div>
              <div className="border border-t-0 rounded-b-md px-3 py-2 text-xs border-indigo-700">
                UG &gt; School of Engineering &amp; Technology &gt; B.Tech. in{' '}
                <span className="font-semibold">{displayValue(studentData?.stream)}</span>
              </div>
            </div>

            {/* ===== SECTION 2: PERSONAL DETAILS ===== */}
            <div className="mb-3">
              <div className="bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded-t-md">
                PERSONAL DETAILS
              </div>
              <div className="border border-t-0 rounded-b-md px-3 py-2 border-purple-700">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                  <p><strong>Full Name:</strong> {displayValue(studentData?.student_name)}</p>
                  <p><strong>Mobile:</strong> {displayValue(studentData?.mobile_number)}</p>
                  <p><strong>Father:</strong> {displayValue(studentData?.father_name)}</p>
                  <p><strong>Mother:</strong> {displayValue(studentData?.mother_name)}</p>
                  <p><strong>DOB:</strong> {displayValue(studentData?.date_of_birth)}</p>
                  <p><strong>Caste:</strong> {displayValue(studentData?.caste)}</p>
                  <p><strong>Religion:</strong> {displayValue(studentData?.religion)}</p>
                  <p><strong>Email:</strong> {displayValue(studentData?.email)}</p>
                  <p><strong>Parent Mobile:</strong> {displayValue(studentData?.alternate_mobile_number)}</p>
                  <p><strong>Aadhaar:</strong> {displayValue(studentData?.aadhar)}</p>
                </div>
              </div>
            </div>

            {/* ===== SECTION 3: ADDRESS DETAILS ===== */}
            <div className="mb-3">
              <div className="bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded-t-md">
                ADDRESS DETAILS
              </div>
              <div className="border border-t-0 rounded-b-md px-3 py-2 border-purple-700">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                  <p><strong>Address:</strong> {displayValue(studentData?.address)}</p>
                  <p><strong>City:</strong> {displayValue(studentData?.city)}</p>
                  <p><strong>District:</strong> {displayValue(studentData?.district)}</p>
                  <p><strong>State:</strong> {displayValue(studentData?.state)}</p>
                  <p><strong>Country:</strong> India</p>
                  <p><strong>PIN Code:</strong> {displayValue(studentData?.pincode)}</p>
                </div>
              </div>
            </div>

            {/* ===== SECTION 4: ONBOARDING FEE PAYMENT ===== */}
            <div className="mb-3">
              <div className="bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-t-md">
                ONBOARDING FEE PAYMENT
              </div>
              <div className="border border-t-0 rounded-b-md px-3 py-2 border-green-700">
                {paymentSuccess ? (
                  <div className="py-4">
                    <div className="text-center mb-4">
                      <div className="flex justify-center mb-2">
                        <CheckCircle size={48} className="text-green-600" />
                      </div>
                      <h3 className="text-lg font-bold text-green-700 mb-1">Payment Successful!</h3>
                      <p className="text-sm text-gray-600">
                        Transaction ID: <span className="font-semibold">{paymentDetails?.transaction_id || 'N/A'}</span>
                      </p>
                      <p className="text-sm text-gray-600">
                        UTR Number: <span className="font-semibold">{paymentDetails?.utr_number || 'N/A'}</span>
                      </p>
                      <p className="text-sm text-gray-600">
                        Amount: <span className="font-semibold">₹{paymentDetails?.amount || '0'}</span>
                      </p>
                      <p className="text-sm text-gray-600 mb-4">
                        Payment Mode: <span className="font-semibold uppercase">{paymentDetails?.payment_mode || 'Online'}</span>
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-3">
                      <button
                        onClick={handleDownloadReceiptPDF}
                        disabled={generatingPdf}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-white text-xs font-medium rounded-lg transition ${
                          generatingPdf ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        <Download size={16} />
                        {generatingPdf ? 'Generating...' : 'Download PDF'}
                      </button>
                      <button
                        onClick={handlePrintReceipt}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition"
                      >
                        <Printer size={16} />
                        Print
                      </button>
                      <button
                        onClick={handleViewReceipt}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition"
                      >
                        <Eye size={16} />
                        View Receipt
                      </button>
                      {/* NEW: View Form button - same concept as Student Report's "View Form" */}
                      <button
                        onClick={handleViewApplicationForm}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition"
                      >
                        <FileText size={16} />
                        View Form
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* QR Code Section - Always visible for both modes */}
                    <div className="flex flex-col items-center justify-center">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">Scan QR Code to Pay</h4>
                      <div className="border border-gray-300 rounded-lg p-4 text-center bg-gray-50">
                        <img
                          src={paymentscan}
                          alt="Scan QR Code"
                          className="mx-auto w-full max-w-[200px] h-auto object-contain"
                        />
                        <p className="text-[10px] text-gray-500 mt-2">Scan this QR code to make payment</p>
                        {paymentData.payment_mode === 'offline' && (
                          <p className="text-[10px] text-orange-500 mt-1 font-medium">ℹ️ Offline Mode - QR for reference only</p>
                        )}
                      </div>
                    </div>

                    {/* Payment Form */}
                    <div>
                      <div className="space-y-1.5">
                        {/* Payment Mode Selection */}
                        <div>
                          <label className={labelClass}>Payment Mode <span className="text-red-500">*</span></label>
                          <div className="flex gap-4 mt-1">
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                              <input
                                type="radio"
                                name="payment_mode"
                                value="online"
                                checked={paymentData.payment_mode === 'online'}
                                onChange={() => handlePaymentModeChange('online')}
                                className="w-3.5 h-3.5 text-green-600 border-gray-300 focus:ring-green-500"
                              />
                              <span className="font-medium">Online</span>
                            </label>
                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                              <input
                                type="radio"
                                name="payment_mode"
                                value="offline"
                                checked={paymentData.payment_mode === 'offline'}
                                onChange={() => handlePaymentModeChange('offline')}
                                className="w-3.5 h-3.5 text-green-600 border-gray-300 focus:ring-green-500"
                              />
                              <span className="font-medium">Cash</span>
                            </label>
                          </div>
                        </div>

                        {/* Amount Field - Always required */}
                        <div>
                          <label className={labelClass}>Amount (₹) <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            name="amount"
                            value={paymentData.amount}
                            onChange={handlePaymentChange}
                            className={inputClass}
                            placeholder="Enter amount in rupees"
                            min="0"
                            step="1"
                            required
                          />
                        </div>

                        {/* UTR Number - Required for Online, Optional for Offline */}
                        <div>
                          <label className={labelClass}>
                            UTR Number 
                            {isOnlineMode && <span className="text-red-500">*</span>}
                            {!isOnlineMode && <span className="text-gray-400 text-[10px] ml-1">(Optional)</span>}
                          </label>
                          <input
                            name="utr_number"
                            value={paymentData.utr_number}
                            onChange={handlePaymentChange}
                            className={`${inputClass} ${!isOnlineMode ? 'border-dashed border-gray-400' : ''}`}
                            placeholder={isOnlineMode ? "Enter UTR Number" : "Enter UTR Number (Optional)"}
                            required={isOnlineMode}
                          />
                        </div>

                        {/* Transaction ID - Required for Online, Optional for Offline */}
                        <div>
                          <label className={labelClass}>
                            Transaction ID
                            {isOnlineMode && <span className="text-red-500">*</span>}
                            {!isOnlineMode && <span className="text-gray-400 text-[10px] ml-1">(Optional)</span>}
                          </label>
                          <input
                            name="transaction_id"
                            value={paymentData.transaction_id}
                            onChange={handlePaymentChange}
                            className={`${inputClass} ${!isOnlineMode ? 'border-dashed border-gray-400' : ''}`}
                            placeholder={isOnlineMode ? "Enter Transaction ID" : "Enter Transaction ID (Optional)"}
                            required={isOnlineMode}
                          />
                        </div>

                        {/* Upload Payment Receipt - Required for Online, Optional for Offline */}
                        <div>
                          <label className={labelClass}>
                            Upload Payment Receipt
                            {isOnlineMode && <span className="text-red-500">*</span>}
                            {!isOnlineMode && <span className="text-gray-400 text-[10px] ml-1">(Optional)</span>}
                          </label>
                          <div
                            className={`border-2 border-dashed rounded p-2 text-center cursor-pointer transition ${
                              isOnlineMode 
                                ? 'border-gray-300 hover:border-blue-500' 
                                : 'border-gray-300 opacity-60 hover:border-gray-400'
                            }`}
                            onClick={() => receiptRef.current?.click()}
                          >
                            {paymentData.payment_receipt ? (
                              <p className="text-xs text-green-600">Selected: {paymentData.payment_receipt.name}</p>
                            ) : (
                              <p className="text-xs text-gray-500">
                                {isOnlineMode ? 'Click to upload receipt (jpg/png)' : 'Upload receipt (Optional)'}
                              </p>
                            )}
                            <input
                              ref={receiptRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={handleFileChange}
                            />
                          </div>
                          {preview && (
                            <div className="mt-1">
                              <div className="border border-gray-200 rounded overflow-hidden max-w-[100px]">
                                <img src={preview} alt="Receipt preview" className="w-full h-auto max-h-24 object-contain" />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Mode-specific hints */}
                        {isOnlineMode ? (
                          <p className="text-[10px] text-red-500 text-center">
                            ⚠️ Please make sure to attach your payment receipt and enter correct UTR and Transaction ID.
                          </p>
                        ) : (
                          <p className="text-[10px] text-blue-500 text-center">
                            ℹ️ Offline mode: Only Amount is required. UTR, Transaction ID & Receipt are optional.
                          </p>
                        )}

                        {/* Submit Button */}
                        <button
                          onClick={handlePaymentSubmit}
                          disabled={paymentLoading}
                          className={`w-full py-1.5 rounded-lg text-white font-medium text-xs transition ${
                            paymentLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {paymentLoading ? 'Processing...' : 'Submit Payment'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ===== SECTION 5: DOCUMENTS CHECKLIST ===== */}
            <div className="mb-0">
              <div className="bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-t-md flex justify-between items-center">
                <span>DOCUMENTS CHECKLIST</span>
                <div className="flex gap-1.5">
                  <button
                    onClick={handleResetAll}
                    className="px-2 py-0.5 bg-red-600 hover:bg-red-700 text-white text-[10px] rounded transition"
                  >
                    Reset All
                  </button>
                  <button
                    onClick={handleCheckAll}
                    className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white text-[10px] rounded transition"
                  >
                    Check All
                  </button>
                </div>
              </div>
              <div className="border border-t-0 rounded-b-md px-3 py-2 border-blue-700">
                <p className="text-[10px] text-gray-500 mb-2">
                  (Original Documents + 2 Sets of Self-Attested Photocopies)
                </p>

                <div className="mb-2">
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="text-[10px] font-medium text-gray-700">
                      Progress: {checkedItems}/{totalItems} Verified
                    </span>
                    <span className="text-[10px] font-medium text-blue-600">
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {documentItems.map((item) => (
                    <label
                      key={item.key}
                      className={`flex items-center justify-between p-2 rounded border transition cursor-pointer ${
                        checklistData?.[item.key] === 1
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checklistData?.[item.key] === 1}
                          onChange={() => toggleChecklistItem(item.key)}
                          className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                        />
                        <span className={`text-[10px] font-medium ${
                          checklistData?.[item.key] === 1 
                            ? 'text-gray-800' 
                            : 'text-gray-600'
                        }`}>
                          {item.label}
                        </span>
                      </div>
                      {checklistData?.[item.key] === 1 && (
                        <CheckCircle size={12} className="text-green-600" />
                      )}
                    </label>
                  ))}
                </div>

                <div className="mt-3 flex justify-center">
                  {/* 🔥 UPDATED: Save/Update Checklist Button with Payment Condition */}
                  <button
                    onClick={handleSaveChecklist}
                    disabled={isSaveDisabled}
                    className={`px-6 py-2 rounded-lg text-white font-medium text-sm transition flex items-center gap-2 ${
                      isSaveDisabled
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-yellow-500 hover:bg-yellow-600'
                    }`}
                    title={
                      !paymentSuccess
                        ? '⚠️ Please complete the onboarding fee payment first before saving the checklist.'
                        : !isAtLeastOneItemSelected()
                        ? 'Please select at least one document before saving.'
                        : ''
                    }
                  >
                    {saving ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {checklistExists ? 'Update Checklist' : 'Save Checklist'}
                      </>
                    )}
                  </button>
                </div>

                {/* 🔥 Show payment required message */}
                {!paymentSuccess && (
                  <p className="text-center text-[10px] text-red-500 mt-2 font-medium">
                    ⚠️ You must complete the onboarding fee payment before saving the checklist.
                  </p>
                )}

              </div>
            </div>
          </>
        )}
      </div>

      {/* ===== RECEIPT MODAL ===== */}
      {showReceiptModal && receiptImageUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-lg font-bold text-gray-800">Payment Receipt</h3>
              <button
                onClick={closeReceiptModal}
                className="text-gray-600 hover:text-gray-900 transition"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 flex justify-center">
              <iframe
                src={receiptImageUrl}
                className="w-full h-[70vh] rounded-lg border border-gray-200"
                title="Payment Receipt"
              />
            </div>
          </div>
        </div>
      )}

      {/* ===== APPLICATION FORM MODAL ===== */}
      {showAppFormModal && studentData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
            <div className="no-print sticky top-0 bg-white border-b px-6 py-3 flex justify-between items-center z-10">
              <h3 className="text-base font-bold text-gray-800">
                Application Form - {displayValue(studentData.student_name)}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadApplicationFormPDF}
                  disabled={generatingAppFormPdf}
                  className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-3 py-1.5 rounded-md text-xs font-medium transition"
                >
                  <Download size={14} />
                  {generatingAppFormPdf ? 'Generating...' : 'Download PDF'}
                </button>
                <button
                  onClick={handlePrintApplicationForm}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition"
                >
                  <Printer size={14} /> Print
                </button>
                <button
                  onClick={closeAppFormModal}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X size={18} />
                </button>
              </div>
            </div>


<div
  id="application-form-print"
  ref={appFormRef}
  className="p-4 bg-white"
  style={{ fontSize: '11px' }}
>
  {/* Header */}
  <div
    className="flex items-center justify-between border-b pb-2 mb-3"
    style={{ borderColor: '#e5e7eb' }}
  >
    <div className="flex-shrink-0">
      <img
        src={ghrLogo}
        alt="University Logo"
        className="h-10 w-auto"
      />
    </div>
    <div className="text-right flex-1 ml-3">
      <p
        className="font-bold text-sm"
        style={{ color: '#3b1a6b' }}
      >
        GH Raisoni University, Saikheda
      </p>
      <p
        className="text-xs font-medium app-form-gold-text"
        style={{ color: '#f5d87a' }}
      >
        Application Form - Admission 2026–27
      </p>
    </div>
  </div>

  {/* Success Message */}
  <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded app-form-success">
    <p className="text-xs font-medium text-green-800 text-center">
      ✅ <span className="font-bold">{displayValue(studentData.student_name)}</span> has been successfully reported at GH Raisoni University, Saikheda!
    </p>
  </div>

  {/* Course Section */}
  <div className="mb-2">
    <div
      className="app-form-header text-white text-[10px] font-semibold px-3 py-1 rounded-t"
      style={{ background: '#3b1a6b' }}
    >
      COURSE APPLYING FOR
    </div>
    <div
      className="border border-t-0 rounded-b px-3 py-2 text-xs"
      style={{ borderColor: '#3b1a6b' }}
    >
      UG &gt; School of Engineering &amp; Technology &gt; B.Tech. in{' '}
      <span className="font-semibold">{displayValue(studentData.stream)}</span>
    </div>
  </div>

  {/* Personal Details - Grid with proper alignment */}
  <div className="mb-2">
    <div
      className="app-form-subheader text-white text-[10px] font-semibold px-3 py-1 rounded-t"
      style={{ background: '#4e2280' }}
    >
      PERSONAL DETAILS
    </div>
    <div
      className="border border-t-0 rounded-b px-3 py-2"
      style={{ borderColor: '#4e2280' }}
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Full Name:</span>
          <span>{displayValue(studentData.student_name)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Mobile:</span>
          <span>{displayValue(studentData.mobile_number)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Father:</span>
          <span>{displayValue(studentData.father_name)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Mother:</span>
          <span>{displayValue(studentData.mother_name)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">DOB:</span>
          <span>{displayValue(studentData.date_of_birth)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Caste:</span>
          <span>{displayValue(studentData.caste)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Religion:</span>
          <span>{displayValue(studentData.religion)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Nationality:</span>
          <span>{displayValue(studentData.nationality || 'Indian')}</span>
        </div>
        <div className="flex col-span-2">
          <span className="font-semibold w-24 flex-shrink-0">Email:</span>
          <span>{displayValue(studentData.email)}</span>
        </div>
        <div className="flex col-span-2">
          <span className="font-semibold w-24 flex-shrink-0">Parent Mobile:</span>
          <span>{displayValue(studentData.alternate_mobile_number)}</span>
        </div>
        <div className="flex col-span-2">
          <span className="font-semibold w-24 flex-shrink-0">Aadhaar:</span>
          <span>{displayValue(studentData.aadhar)}</span>
        </div>
      </div>
    </div>
  </div>

  {/* Address Details */}
  <div className="mb-2">
    <div
      className="app-form-subheader text-white text-[10px] font-semibold px-3 py-1 rounded-t"
      style={{ background: '#4e2280' }}
    >
      ADDRESS DETAILS
    </div>
    <div
      className="border border-t-0 rounded-b px-3 py-2"
      style={{ borderColor: '#4e2280' }}
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div className="flex col-span-2">
          <span className="font-semibold w-24 flex-shrink-0">Address:</span>
          <span>{displayValue(studentData.address)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">City:</span>
          <span>{displayValue(studentData.city)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">District:</span>
          <span>{displayValue(studentData.district)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">State:</span>
          <span>{displayValue(studentData.state)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">Country:</span>
          <span>India</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-24 flex-shrink-0">PIN Code:</span>
          <span>{displayValue(studentData.pincode)}</span>
        </div>
      </div>
    </div>
  </div>

  {/* Payment Details */}
  <div className="mb-2">
    <div
      className="app-form-header text-white text-[10px] font-semibold px-3 py-1 rounded-t"
      style={{ background: '#3b1a6b' }}
    >
      PAYMENT DETAILS
    </div>
    <div
      className="border border-t-0 rounded-b px-3 py-2"
      style={{ borderColor: '#3b1a6b' }}
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div className="flex">
          <span className="font-semibold w-28 flex-shrink-0">Status:</span>
          <span className={`inline-block px-2 py-0.5 text-[9px] font-medium rounded-full ${paymentSuccess ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {paymentSuccess ? 'Success' : 'Pending'}
          </span>
        </div>
        <div className="flex">
          <span className="font-semibold w-28 flex-shrink-0">Amount:</span>
          <span>₹{displayValue(paymentDetails?.amount)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-28 flex-shrink-0">Mode:</span>
          <span className="uppercase">{displayValue(paymentDetails?.payment_mode)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-28 flex-shrink-0">Transaction ID:</span>
          <span>{displayValue(paymentDetails?.transaction_id)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-28 flex-shrink-0">UTR Number:</span>
          <span>{displayValue(paymentDetails?.utr_number)}</span>
        </div>
        <div className="flex">
          <span className="font-semibold w-28 flex-shrink-0">Date:</span>
          <span>{paymentDetails?.payment_date ? new Date(paymentDetails.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span>
        </div>
      </div>
    </div>
  </div>

  {/* Documents Checklist - Improved with proper spacing */}
  <div className="mb-2">
    <div
      className="app-form-subheader text-white text-[10px] font-semibold px-3 py-1 rounded-t"
      style={{ background: '#4e2280' }}
    >
      DOCUMENTS CHECKLIST
    </div>
    <div
      className="border border-t-0 rounded-b px-3 py-2"
      style={{ borderColor: '#4e2280' }}
    >
      <p className="text-[9px] text-gray-500 mb-2">
        Verified: {checkedItems}/{totalItems} ({progress}%)
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
        {documentItems.map((item) => {
          const isChecked = checklistData?.[item.key] === 1;
          return (
            <div key={item.key} className="flex items-center gap-2 py-0.5">
              {isChecked ? (
                <CheckCircle size={12} className="text-green-600 flex-shrink-0" />
              ) : (
                <XCircle size={12} className="text-red-400 flex-shrink-0" />
              )}
              <span className={isChecked ? 'text-gray-800' : 'text-gray-500'}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  </div>

  {/* Declaration */}
  <div className="mb-2">
    <div
      className="app-form-subheader text-white text-[10px] font-semibold px-3 py-1 rounded-t"
      style={{ background: '#4e2280' }}
    >
      DECLARATION
    </div>
    <div
      className="border border-t-0 rounded-b px-3 py-2 text-[10px]"
      style={{ borderColor: '#4e2280' }}
    >
      <p className="text-gray-700 leading-relaxed">
        I hereby declare that the information furnished by me in the admission application submitted to GH Raisoni University, Saikheda is true and correct to the best of my knowledge and belief. I understand that any false or misleading information may result in the cancellation of my admission.
      </p>
    </div>
  </div>

  {/* Footer */}
  <div
    className="flex justify-between items-end border-t pt-2 mt-2 text-[10px]"
    style={{ borderColor: '#e5e7eb' }}
  >
    <div>
      <p className="text-gray-700">Applicant:</p>
      <p className="font-semibold text-sm" style={{ color: '#3b1a6b' }}>
        {displayValue(studentData.student_name)}
      </p>
    </div>
    <div className="text-right">
      <p className="text-gray-700">Date:</p>
      <p className="font-semibold text-sm" style={{ color: '#3b1a6b' }}>
        {new Date().toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </p>
    </div>
  </div>
</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsChecklist;